<template>
  <!-- prettier-ignore -->
  <ovp-text-input
    ref="input"
    v-bind="$attrs"
    :autocomplete="autocomplete"
    :autofocus="autofocus"
    :class="classes"
    :delay="750"
    :hint="autoHint"
    :name="name"
    :readonly="readonly"
    :required="required"
    :theme="theme"
    :title="title"
    :value="displayName"
    :validate="validate"
    @focus="reopen"
    @keydown.native="handleKeyEvent"
    @update="search">
    <transition
      mode="out-in"
      name="slide-down">
      <div
        class="zipchooser-results"
        v-if="results.length > 0">
        <button
          type="button"
          :class="{active: index === _index}"
          :key="result.displayName"
          v-for="(result, _index) in results"
          @click="select(result)"
          @mouseenter="hover(_index)">
          {{ result.displayName }}
        </button>
      </div>
    </transition>

    <div
      class="zipchooser__loader"
      v-show="busy"><span>⚆</span></div>
  </ovp-text-input>
</template>

<script>
import debounce from 'lodash/debounce'
import { DEFAULT_DELAY_TIME } from '@/config/constants'

export default {
  name: 'OvpZipChooser',

  model: {
    prop: 'value',
    event: 'update'
  },

  data () {
    return {
      busy: false,
      error: false,
      index: 0,
      results: false,
      searchValue: null
    }
  },

  props: {
    autocomplete: {
      type: String,
      default: 'off'
    },

    autofocus: {
      type: Boolean,
      default: false
    },

    contractStartDate: {
      type: String,
      required: true
    },

    hint: {
      type: String,
      required: false
    },

    name: {
      type: String,
      default: 'location'
    },

    readonly: {
      type: Boolean,
      default: false
    },

    replace: {
      type: Boolean,
      default: false
    },

    required: {
      type: Boolean,
      default: false
    },

    theme: {
      type: String,
      default: 'light'
    },

    title: {
      type: String,
      required: true
    },

    validate: {
      type: Object,
      default: () => {}
    },

    value: {
      type: Object,
      required: true
    }
  },

  computed: {
    autoHint () {
      if (this.results.length === 0 && !this.busy) {
        return this.$t('location.noresults')
      } else if (this.error) {
        return this.$t('location.error')
      } else {
        return this.hint
      }
    },

    classes () {
      return {
        'zipchooser': true,
        [`zipchooser--${this.theme}`]: true,
        'zipchooser--invalid': this.error || this.results.length === 0
      }
    },

    displayName () {
      if (this.searchValue !== null) return this.searchValue
      if (this.value.displayName) return this.value.displayName
    }
  },

  methods: {
    clear () {
      this.error = false
      this.index = null
      this.results = false
      this.searchValue = null
    },

    reopen () {
      if (this.value?.zipCode) this.search(this.value?.zipCode, false)
    },

    getLocation (query) {
      return new Promise((resolve, reject) => {
        const payload = {
          query,
          referenceDate: this.contractStartDate
        }

        this.$backend.masterdata.getLocations(payload)
          .then(response => resolve(response.data))
          .catch(error => reject(error))
      })
    },

    handleKeyEvent (event) {
      switch (event.keyCode) {
        case 9: // tab
          this.select(this.results[this.index])
          break

        case 13: // enter
          event.preventDefault()
          this.select(this.results[this.index])
          break

        case 27: // esc
          event.preventDefault()
          this.clear()
          break

        case 38: // up
          event.preventDefault()
          this.mark(-1)
          break

        case 40: // down
          event.preventDefault()
          this.mark(+1)
          break
      }
    },

    hover (index) {
      this.index = index
    },

    mark (sum) {
      let index
      this.index = index = (this.index === null) ? 0 : this.index + sum

      if (!this.results[index]) {
        this.index = (sum === -1) ? 0 : this.results.length - 1
      }
    },

    search (value, set = true) {
      if (value === this.searchValue) return
      if (this.value?.displayName === value) return

      if (set) {
        this.validate?.$reset()
        this.searchValue = value
      }

      if (value.length >= 3) {
        this.error = false
        this.busy = true
        this.getLocation(value)
          .then(results => {
            if (results.length === 1) {
              this.select(results[0])
            } else {
              this.results = results
            }
          })
          .catch(error => this.error = true)
          .finally(() => this.busy = false)
      }
    },

    select (item) {
      if (!item) return

      const updateItem = this.replace ? item : Object.assign({}, this.value, item)

      this.$nextTick(() => {
        this.clear()
        this.$emit('update', updateItem)
      })
    }
  }
}
</script>

<style name="animations" scoped>
.slide-down-enter-active,
.slide-down-leave-active,
.slide-up-enter-active,
.slide-up-leave-active {
  transition-duration: .5s;
  transition-property: height, opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}

.slide-down-enter,
.slide-up-leave-active,
.slide-down-leave-active,
.slide-up-enter {
  opacity: 0;
  height: 0;
  transform: translate(0, -2em);
}
</style>

<style name="mobile" scoped>
.zipchooser {
  position: relative;

  &--invalid {
    color: var(--c-error);

    & >>> .input__hint {
      color: var(--c-error);
    }
  }

  &-results {
    position: absolute;
    z-index: 10;

    width: 389px;
    max-height: 190px;

    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .14), 0 3px 5px 0 rgba(0, 0, 0, .12), 0 3px 1px -2px rgba(0, 0, 0, .2);
    overflow-y: auto;

    background-color: var(--c-white);

    button {
      border: 0;
      outline: 0;
      background: none;
      padding: 10px 15px;
      width: 100%;
      text-align: left;
      cursor: pointer;
      transition: background-color .1s ease-in-out;
      font-weight: normal;
      font-size: 16px;
      line-height: 20px;

      &:hover, &.active {
        background-color: var(--c-gray);
      }
    }
  }

  &__loader {
    position: absolute;
    right: 0;
    top: 10px;
    color: var(--c-black);
    z-index: 2;

    height: 35px;
    width: 35px;
    line-height: 35px;
    font-size: 20px;
    text-align: center;
    cursor: wait;
    background-color: var(--c-white);

    span {
      display: inline-block;
      animation: spin 2s linear infinite;
    }
  }

  &--dark {
    .zipchooser__loader {
      background-color: var(--c-warm-gray);
    }
  }

  @keyframes spin {
    100% { -webkit-transform: rotate(360deg); transform:rotate(360deg); }
  }
}
</style>
